import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { TextEditor } from "@src/components/ui-kit/TextEditor/TextEditor";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import type { SendEmailForm } from "../models/send-invoice-form";

type SendEmailModalProps = {
  form: SendEmailForm | undefined;
  title: ReactNode;
  isEditable: boolean;
};

export const SendEmailModal = observer(function SendEmailModal({
  form,
  title,
  isEditable,
}: SendEmailModalProps) {
  if (!form) return null;

  return (
    <Modal
      isCentered
      isOpen={form.modalState.isOpen}
      onClose={form.handleModalClose}
      scrollBehavior="inside"
      size="3xl"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {title}
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <VStack align="start" w="full">
            <FormControl isInvalid={form.$.recipients.hasError}>
              <FormLabel>
                <Trans>Recipients</Trans>
              </FormLabel>
              <Textarea
                resize="vertical"
                isDisabled={!isEditable}
                onChange={({ target }) => {
                  form.$.recipients.onChange(target.value);
                }}
                value={form.$.recipients.value}
              />
              <HStack align="center" mt="2">
                <Icon name="info-circle" />
                <FormHelperText>
                  <Trans>
                    If you use multiple emails, please separate them with ", "
                  </Trans>
                </FormHelperText>
              </HStack>
              <FormErrorMessage>{form.$.recipients.error}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={form.$.subject.hasError}>
              <FormLabel>
                <Trans>Subject</Trans>
              </FormLabel>
              <Input
                isDisabled={!isEditable}
                onChange={({ target }) => {
                  form.$.subject.onChange(target.value);
                }}
                value={form.$.subject.value}
              />
              <FormErrorMessage>{form.$.subject.error}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={form.$.content.hasError}>
              <FormLabel>
                <Trans>Content</Trans>
              </FormLabel>
              <TextEditor
                attachments={[]}
                initialValue={form.$.content.value}
                onChange={(val) => {
                  form.$.content.onChange(val?.toString() ?? "");
                }}
                borderWidth="thin"
                px="3"
                borderColor="grey.200"
                borderRadius="base"
                transition="border-color 150ms ease-in"
                isEditable={isEditable}
                isDisabled={!isEditable}
                _focusWithin={{
                  borderColor: "purple.500",
                }}
              />
              <FormErrorMessage>{form.$.content.error}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button
              colorScheme="gray"
              onClick={form.handleModalClose}
              variant="subtle"
            >
              <Trans>Cancel</Trans>
            </Button>
            {isEditable && (
              <Button
                isDisabled={form.hasError}
                onClick={form.handleModalConfirm}
              >
                <Trans>Save</Trans>
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
